import styles from './SideNavigationSearchBar.module.scss';
import {IconArrowUpBlock, IconClose, IconSearch} from '../../../icons';
import React, {ChangeEvent, forwardRef, useEffect} from 'react';
import {SearchContextType} from '../../side-navigation-contexts/SideNavigationSearchContext';

export interface SideNavigationSearchBarProps {
    /** The text to be displayed on the search bar before any input is given. */
    placeholder?: string;
    /** Method to focus the input element. */
    handleFocusingSearch: () => void;
    /** Method to clear the search value. */
    handleSearchClear: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /** The current value of the search input. */
    searchValue: SearchContextType['searchValue'];
    /** Method to set the search value. */
    setSearchValue: SearchContextType['setSearchValue'];
    /** Whether the search bar is focused. */
    isFocused: boolean;
    /** Method to set whether the search bar is focused. */
    setIsFocused: (value: boolean) => void;
}

export const SideNavigationSearchBar = forwardRef<HTMLInputElement, SideNavigationSearchBarProps>(
    ({
         placeholder,
         searchValue,
         setSearchValue,
         handleFocusingSearch,
         handleSearchClear,
         isFocused,
         setIsFocused
     }, ref) => {

        const searchShortCutIcon = (searchValue === '' || searchValue === undefined) && !isFocused ?
            <span className={styles.searchShortcutIconContainer}><IconArrowUpBlock
                className={styles.searchShortcutIcon}/>
                <span className={styles.searchShortcutText}>
                    F
                </span>
            </span> : <></>;

        const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
            setSearchValue(event.target.value);
        }

        useEffect(() => {
            // Add event listener for Shift + F key press
            const handleShortcut = (event: KeyboardEvent) => {
                if (event.shiftKey && event.key.toLowerCase() === 'f' && !(document.activeElement instanceof HTMLTextAreaElement || document.activeElement instanceof HTMLInputElement)) {
                    event.preventDefault();
                    handleFocusingSearch();
                }
            };

            document.addEventListener('keydown', handleShortcut);

            return () => {
                // Remove the event listener when the component unmounts
                document.removeEventListener('keydown', handleShortcut);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return (
            <span className={styles.searchBarContainer} data-text={searchValue} onClick={handleFocusingSearch}>
                <span className={styles.iconAndInputContainer}>
                    <IconSearch className={styles.searchIcon}/>
                    <span className={styles.inputAndClearContainer}>
                        <input
                            aria-label={'Search the side navigation.'}
                            className={styles.input}
                            type={'search'}
                            placeholder={placeholder}
                            onChange={(event) => handleInputChange(event)}
                            ref={ref}
                            value={searchValue}
                            autoComplete={'off'}
                            onBlur={() => setIsFocused(false)}
                        />
                        {searchValue !== '' && searchValue !== undefined ?
                            <button onClick={(event) => handleSearchClear(event)}
                                    aria-label={'Clear side navigation search.'}
                                    className={styles.unsetButton}>
                                <IconClose className={styles.inputClose}/>
                            </button> : <></>
                        }
                        {searchShortCutIcon}
                    </span>
                </span>
            </span>
        );
    }
);

SideNavigationSearchBar.displayName = 'SideNavigationSearchBar';